import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, Button } from "@ann/web-components";
import { UsersContainer, NameCell } from "./Users.styled";
import { UserTableSection } from "./components";
import EmptyUsersList from "../../../Main/pages/Users/components/EmptyUsersList";
import { getUserList } from "../../../../services";
import { formatLastLogin } from "../../../../utils";
import { useDebounce } from "../../../../../../../hooks";

// TODO: Set to true when User Detail view is ready
const ENABLE_USER_DETAIL_NAVIGATION = false;

const Users = ({ roleId, roleData }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState("createdAt");
  const [orderDirection, setOrderDirection] = useState("DESC");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
  });
  const [searchKeyword, setSearchKeyword] = useState("");
  
  // Debounce search keyword
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  // Fetch users from API filtered by roleId
  useEffect(() => {
    const fetchUsers = async () => {
      if (!roleId) return;
      
      setLoading(true);
      try {
        const response = await getUserList({
          page: currentPage,
          page_size: itemsPerPage,
          order_by: orderBy,
          order_direction: orderDirection,
          include_deleted: false,
          search: debouncedSearchKeyword,
          // Filters for users assigned to this role
          statuses: ["ACTIVE", "IN_PROGRESS"],
          roleUuids: [roleId],
        });

        if (response && response.body) {
          setUsers(response.body);
          if (response.meta) {
            setMeta({
              totalItems: response.meta.totalItems || 0,
              totalPages: response.meta.totalPages || 0,
              currentPage: response.meta.currentPage || currentPage,
              perPage: response.meta.perPage || itemsPerPage,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentPage, itemsPerPage, orderBy, orderDirection, debouncedSearchKeyword, roleId]);

  const handleNameClick = (userId, userName) => {
    navigate(`/v2/user-management/users/${userId}`, {
      state: { userName }
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSortChange = (selectedOption) => {
    const validOrderBy = ["createdAt", "email", "fullName", "status", "updatedAt"];
    const newOrderBy = validOrderBy.includes(selectedOption) ? selectedOption : "createdAt";
    setOrderBy(newOrderBy);
    setCurrentPage(1);
  };

  const handleOrderChange = (currentOrder) => {
    // Toggle order: asc -> desc, desc -> asc
    const newOrder = currentOrder === 'asc' ? 'DESC' : 'ASC';
    setOrderDirection(newOrder);
    setCurrentPage(1);
  };

  const handleSearchChange = (keyword) => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
  };

  const handleInviteUsers = () => {
    navigate("/v2/user-management/invite-user");
  };

  const handleSelectionChange = (cells) => {
    // Handle selection if needed
  };

  const getAccountStatusColor = (status) => {
    const normalizedStatus = typeof status === 'string' ? status.toUpperCase() : '';
    
    switch (normalizedStatus) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
        return 'alert';
      case 'INACTIVE':
        return 'neutral';
      case 'DELETED':
        return 'error';
      case 'EXPIRED':
        return 'error';
      default:
        return 'neutral';
    }
  };

  // Map API response to table data
  const tableData = users.map((user) => {
    // Programación defensiva: soportar ambos formatos (uuid y userUuid)
    const userId = user.uuid || user.userUuid || user.id || user.user_uuid;
    
    // Programación defensiva: status puede ser null en el nuevo formato
    const statusDisplay = user.status 
      ? user.status.charAt(0).toUpperCase() + user.status.slice(1).toLowerCase() 
      : "—";

    return {
      id: userId,
      name: ENABLE_USER_DETAIL_NAVIGATION ? (
        <NameCell onClick={() => handleNameClick(userId, user.fullName || user.full_name)}>
          {user.fullName || user.full_name || "—"}
        </NameCell>
      ) : (
        user.fullName || user.full_name || "—"
      ),
      email: user.email || "—",
      assignedBy: user.assignedBy || "John Doe",
      assignmentDate: user.assignmentDate ? formatLastLogin(user.assignmentDate) : "—",
      userStatus: (
        <Tag
          color={getAccountStatusColor(user.status)}
          size="small"
          text={statusDisplay}
        />
      ),
      // Programación defensiva: updatedAt puede no venir en el nuevo formato
      lastLogin: (user.updatedAt || user.updated_at) ? formatLastLogin(user.updatedAt || user.updated_at) : "—",
    };
  });

  const headers = [
    { label: 'Name', value: 'name' },
    { label: 'Email', value: 'email' },
    { label: 'Assigned by', value: 'assignedBy' },
    { label: 'Assignment date', value: 'assignmentDate' },
    { label: 'User status', value: 'userStatus' },
    { label: 'Last Login', value: 'lastLogin' }
  ];

  const getSelectedSortOption = () => {
    const validOrderBy = ["createdAt", "email", "fullName", "status", "updatedAt"];
    return validOrderBy.includes(orderBy) ? orderBy : "createdAt";
  };

  const getOrderDirection = () => {
    return orderDirection.toLowerCase();
  };

  // Show empty state only when NOT loading AND there are no users
  const showEmptyState = !loading && tableData.length === 0;

  return (
    <UsersContainer>
      {showEmptyState ? (
        <EmptyUsersList onInviteUsers={handleInviteUsers} />
      ) : (
        <UserTableSection
          tableData={tableData}
          headers={headers}
          onSelectionChange={handleSelectionChange}
          currentPage={meta.currentPage || currentPage}
          itemsPerPage={meta.perPage || itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          onPageChange={handlePageChange}
          totalItems={meta.totalItems || 0}
          totalPages={meta.totalPages || 0}
          loading={loading}
          onSortChange={handleSortChange}
          onOrderChange={handleOrderChange}
          selectedSortOption={getSelectedSortOption()}
          orderDirection={getOrderDirection()}
          onSearchChange={handleSearchChange}
        />
      )}
    </UsersContainer>
  );
};

export default Users;
